import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Package, Clock, CheckCircle, Truck, XCircle, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: any }> = {
  pending:   { label: 'Oczekuje',    color: 'oklch(0.72 0.24 45)',   icon: Clock },
  confirmed: { label: 'Potwierdzone', color: 'oklch(0.65 0.32 280)', icon: CheckCircle },
  printing:  { label: 'Drukowanie',  color: 'oklch(0.68 0.28 195)',  icon: Package },
  shipped:   { label: 'Wysłane',     color: 'oklch(0.65 0.26 150)',  icon: Truck },
  delivered: { label: 'Dostarczone', color: 'oklch(0.65 0.26 150)',  icon: CheckCircle },
  cancelled: { label: 'Anulowane',   color: 'oklch(0.62 0.28 15)',   icon: XCircle },
};

interface OrderItem { id: string; title: string; price_pln: number; quantity: number; image_url: string | null; }
interface Order {
  id: string; status: string; total_pln: number; created_at: string;
  full_name: string; street: string; city: string; postal_code: string;
  items?: OrderItem[];
}

export default function OrdersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    if (!user) { navigate('/'); return; }
    async function load() {
      const { data } = await supabase
        .from('orders')
        .select('id, status, total_pln, created_at, full_name, street, city, postal_code')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false });
      setOrders((data ?? []) as Order[]);
      setLoading(false);
    }
    load();
  }, [user, navigate]);

  const loadItems = async (orderId: string) => {
    if (expanded === orderId) { setExpanded(null); return; }
    const { data } = await supabase
      .from('order_items')
      .select('id, title, price_pln, quantity, image_url')
      .eq('order_id', orderId);
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, items: (data ?? []) as OrderItem[] } : o));
    setExpanded(orderId);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <div className="sticky top-0 z-30 flex items-center gap-3 px-4 py-3 border-b"
        style={{ background: 'oklch(0.10 0.022 280 / 0.92)', borderColor: 'oklch(0.22 0.04 280)', backdropFilter: 'blur(14px)' }}>
        <button onClick={() => navigate('/')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm font-semibold"
          style={{ fontFamily: 'Rajdhani, sans-serif' }}>
          <ArrowLeft className="w-4 h-4" /> Strona główna
        </button>
        <span className="text-muted-foreground/40">/</span>
        <span className="font-black tracking-widest text-foreground text-sm" style={{ fontFamily: 'Orbitron, sans-serif' }}>
          MOJE ZAMÓWIENIA
        </span>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
        ) : orders.length === 0 ? (
          <div className="flex flex-col items-center py-20 gap-4 text-muted-foreground">
            <Package className="w-16 h-16 opacity-20" />
            <p style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: '1.1rem' }}>Brak zamówień</p>
            <button onClick={() => navigate('/')}
              className="text-sm text-primary hover:underline" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
              Przejdź do kolekcji →
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <h1 className="text-3xl font-black text-foreground mb-6" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              ZAMÓWIENIA
            </h1>
            {orders.map((order, i) => {
              const cfg = STATUS_CONFIG[order.status] ?? STATUS_CONFIG.pending;
              const Icon = cfg.icon;
              const isExp = expanded === order.id;
              return (
                <motion.div key={order.id}
                  initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }}
                  className="rounded-2xl border overflow-hidden"
                  style={{ background: 'oklch(0.15 0.03 280)', borderColor: 'oklch(0.23 0.04 280)' }}>

                  <div className="flex items-center gap-3 px-5 py-4 cursor-pointer" onClick={() => loadItems(order.id)}>
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `${cfg.color}20` }}>
                      <Icon className="w-4 h-4" style={{ color: cfg.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: cfg.color }}>
                          {cfg.label.toUpperCase()}
                        </span>
                        <span className="text-xs text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          #{order.id.slice(0, 8).toUpperCase()}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                        {new Date(order.created_at).toLocaleDateString('pl-PL', { day: '2-digit', month: 'long', year: 'numeric' })}
                        {' · '}{order.city}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-black text-base" style={{ fontFamily: 'Orbitron, sans-serif', color: 'var(--primary)' }}>
                        {Number(order.total_pln).toFixed(2)} zł
                      </p>
                    </div>
                    {isExp ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                  </div>

                  {isExp && order.items && (
                    <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }}
                      className="border-t px-5 py-4 space-y-3"
                      style={{ borderColor: 'oklch(0.22 0.04 280)' }}>
                      {order.items.map(item => (
                        <div key={item.id} className="flex items-center gap-3">
                          {item.image_url && (
                            <img src={item.image_url} alt={item.title} className="w-10 h-14 object-cover rounded-lg flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-foreground truncate" style={{ fontFamily: 'Rajdhani, sans-serif' }}>{item.title}</p>
                            <p className="text-xs text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                              ×{item.quantity} · {Number(item.price_pln).toFixed(2)} zł/szt.
                            </p>
                          </div>
                          <span className="font-bold text-sm flex-shrink-0" style={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--primary)' }}>
                            {(Number(item.price_pln) * item.quantity).toFixed(2)} zł
                          </span>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
